import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { OUTCOMES, type Outcome, type OutcomePoint } from '../../api/types';

interface Props {
  points: OutcomePoint[];
  loading?: boolean;
}

/**
 * Modern semantic palette — good (emerald) → blocked (red) → gone (zinc),
 * with amber/indigo for the in-between states. Tailwind/Radix saturations
 * for screen-tested vibrance.
 */
const OUTCOME_COLORS: Record<Outcome, string> = {
  completed: '#10b981',    // emerald 500
  partial: '#f59e0b',      // amber 500
  exploratory: '#6366f1',  // indigo 500
  stuck: '#ef4444',        // red 500
  abandoned: '#71717a',    // zinc 500
};

const OUTCOME_LABELS: Record<Outcome, string> = {
  completed: 'Completed',
  partial: 'Partial',
  exploratory: 'Exploratory',
  stuck: 'Stuck',
  abandoned: 'Abandoned',
};

export function OutcomeStackedBarChart({ points, loading }: Props): JSX.Element {
  const dates = points.map((p) => p.date);

  const option: EChartsOption = {
    grid: { left: 48, right: 24, top: 56, bottom: 36 },
    legend: {
      top: 8,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 12 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: {
        formatter: (v: string) => v.slice(5), // MM-DD
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      name: 'Sessions',
      nameLocation: 'middle',
      nameGap: 36,
    },
    series: OUTCOMES.map((outcome) => ({
      name: OUTCOME_LABELS[outcome],
      type: 'bar',
      stack: 'outcome',
      barMaxWidth: 36,
      itemStyle: { color: OUTCOME_COLORS[outcome] },
      data: points.map((p) => p.counts[outcome] ?? 0),
    })),
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 320, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
